import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { mono, PageTitle, Card, Btn, Input } from "./ptracker/ptUi";

// Routing — who owns a piece of work at every stage, and who has actually been
// doing it. Reads the four read-only /api/routing/* endpoints.
//
// Three tabs:
//   OWNERSHIP  the stage->owner table, with the stages that tell nobody today
//   ROSTER     per-brand craft evidence mined from real asset.team history
//   UNOWNED    live assets sitting on a stage with nobody in the owning craft
//
// Nothing here assigns anything — the module is read-only until the roster is
// confirmed. Every mutation the eventual engine will make is previewed, not run.

const TONE = {
  silent: { bg: "rgba(217,119,6,.14)", fg: "#d97706" },
  ok: { bg: "rgba(16,163,12,.12)", fg: "#0ca30c" },
  bad: { bg: "var(--rd)", fg: "var(--r)" },
  idle: { bg: "var(--s2)", fg: "var(--t3)" },
};

// Stages whose transition tells nobody today, per domain.
//  asset — the auto-task branch in server.py covers only Script, Treatment,
//          Edit, Design Brief/V1 and Internal Review; these eight are silent.
//  lead  — PUT /leads/{id}/stage writes stage_history and nothing else, so no
//          lead stage notifies anyone at all.
//  brief — assignment and status-change notifications exist, but nothing fires
//          on create, nothing targets a reviewer at Pitched, and winning a
//          brief reaches production not at all.
const SILENT_TODAY = {
  asset: new Set(["Concept", "Pre-Production", "Shoot Scheduled", "Shooting",
    "Client Review", "Revisions", "Approved", "Live"]),
  lead: new Set(["Cold", "Warm", "Hot", "New", "Engaged", "Qualified", "RFP Received",
    "Proposal Sent", "Follow-up", "Negotiation", "Pitched"]),
  brief: new Set(["New", "Pitched", "Won", "Executed"]),
};

function Pill({ tone = "idle", children }) {
  const t = TONE[tone] || TONE.idle;
  return (
    <span style={{ background: t.bg, color: t.fg, fontFamily: mono, fontSize: 10, fontWeight: 700,
      padding: "2px 7px", borderRadius: 20, letterSpacing: ".06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function Tile({ label, value, sub, tone }) {
  const color = tone === "red" ? "var(--r)" : tone === "amber" ? "#d97706" : tone === "green" ? "#0ca30c" : "var(--t1)";
  return (
    <Card>
      <div style={{ fontSize: 26, fontWeight: 800, color, letterSpacing: "-.02em", lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 600, color: "var(--t3)", letterSpacing: ".08em", textTransform: "uppercase", marginTop: 8 }}>{label}</div>
      {sub && <div style={{ fontFamily: mono, fontSize: 10, color: "var(--t4)", marginTop: 3 }}>{sub}</div>}
    </Card>
  );
}

function Empty({ children }) {
  return <div style={{ fontFamily: mono, fontSize: 12, color: "var(--t3)", padding: "22px 4px" }}>{children}</div>;
}

const th = { textAlign: "left", fontFamily: mono, fontSize: 10, fontWeight: 600, color: "var(--t3)",
  letterSpacing: ".08em", textTransform: "uppercase", padding: "9px 12px", borderBottom: "1px solid var(--bdr)", whiteSpace: "nowrap" };
const td = { padding: "9px 12px", borderBottom: "1px solid var(--bdr)", color: "var(--t2)", fontSize: 13, verticalAlign: "top" };

// ── Ownership ───────────────────────────────────────────────────────────────
function Ownership({ table }) {
  const [scope, setScope] = useState("asset");
  if (!table) return <Empty>Loading the ownership table…</Empty>;

  const gaps = table.coverage_gaps || [];
  const rows = scope === "asset" ? (table.asset_stages || [])
    : scope === "lead" ? (table.lead_stages || [])
    : (table.brief_statuses || []);

  const silentCount = (table.asset_stages || []).filter((r) => SILENT_TODAY.asset.has(r.stage)).length;

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 16 }}>
        <Tile label="Asset stages mapped" value={(table.asset_stages || []).length} />
        <Tile label="Silent today" value={silentCount} tone={silentCount ? "amber" : "green"}
          sub="status moves, nobody told" />
        <Tile label="Coverage gaps" value={gaps.length} tone={gaps.length ? "red" : "green"}
          sub={gaps.length ? "stages with no owner" : "every stage has an owner"} />
        <Tile label="Crafts with an org role" value={Object.keys(table.craft_org_role || {}).length}
          sub="the rest need the roster" />
      </div>

      {gaps.length > 0 && (
        <Card style={{ borderColor: "var(--r)", marginBottom: 16 }}>
          <div style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: "var(--r)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 }}>
            Uncovered stages
          </div>
          <div style={{ fontSize: 13, color: "var(--t2)" }}>
            {gaps.map((g) => `${g.domain}: ${g.stage}`).join(" · ")}
            <div style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)", marginTop: 6 }}>
              Work reaching these stages would have no owner. Add a row in backend/routing.py.
            </div>
          </div>
        </Card>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {[["asset", "Production"], ["lead", "Lead"], ["brief", "Brief"]].map(([k, label]) => (
          <button key={k} onClick={() => setScope(k)}
            style={{ fontFamily: mono, fontSize: 11, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase",
              padding: "7px 13px", borderRadius: "var(--radius-sm)", cursor: "pointer",
              background: scope === k ? "var(--r)" : "transparent", color: scope === k ? "#fff" : "var(--t2)",
              border: scope === k ? "none" : "1px solid var(--bdr)" }}>
            {label}
          </button>
        ))}
      </div>

      <Card style={{ padding: 0, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
          <thead>
            <tr>
              <th style={th}>Stage</th>
              <th style={th}>Owner</th>
              {scope === "asset" && <th style={th}>Clock</th>}
              {scope === "asset" && <th style={th}>Escalates to</th>}
              <th style={th}>Today</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const stage = r.stage || r.status;
              const silent = (SILENT_TODAY[scope] || new Set()).has(stage);
              return (
                <tr key={stage}>
                  <td style={{ ...td, fontFamily: mono, fontSize: 12, color: "var(--t1)", whiteSpace: "nowrap" }}>{stage}</td>
                  <td style={{ ...td, color: "var(--t1)", fontWeight: 600 }}>
                    {r.label || <span style={{ color: "var(--t4)", fontWeight: 400 }}>— closed</span>}
                  </td>
                  {scope === "asset" && <td style={{ ...td, fontFamily: mono, fontSize: 12 }}>{r.sla_days ? `${r.sla_days}d` : "—"}</td>}
                  {scope === "asset" && (
                    <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>
                      {(r.escalates_to || []).join(" → ") || "—"}
                    </td>
                  )}
                  <td style={td}>
                    {!r.craft ? <Pill tone="idle">terminal</Pill>
                      : silent ? <Pill tone="silent">tells nobody</Pill>
                      : <Pill tone="ok">notifies</Pill>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      <p style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)", marginTop: 10 }}>
        SLA days are a starting proposal. Set them from the stage_history timestamps already stored, not by guess.
      </p>
    </>
  );
}

// ── Roster evidence ─────────────────────────────────────────────────────────
const BLOCK_REASON = {
  no_history: "no history",
  unknown_user: "person not found",
  revoked_user: "person revoked",
  too_few_assets: "too few assets",
  no_majority: "no clear lead",
  tied: "tied",
};

function Roster({ data, err }) {
  const [q, setQ] = useState("");
  const [onlyGaps, setOnlyGaps] = useState(false);

  if (err) return <Card style={{ borderColor: "var(--r)" }}><div style={{ color: "var(--r)", fontFamily: mono, fontSize: 12 }}>{err}</div></Card>;
  if (!data) return <Empty>Reading crew history off every asset…</Empty>;

  const brands = (data.by_brand || []).filter((b) => {
    if (q && !b.brand.toLowerCase().includes(q.toLowerCase())) return false;
    if (onlyGaps && !(b.crafts || []).some((c) => !c.suggested)) return false;
    return true;
  });

  const confirmable = (data.by_brand || []).reduce((n, b) => n + (b.crafts || []).filter((c) => c.suggested).length, 0);
  const needsCall = (data.by_brand || []).reduce((n, b) => n + (b.crafts || []).filter((c) => !c.suggested).length, 0);

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 16 }}>
        <Tile label="Assets scanned" value={data.assets_scanned ?? 0}
          sub={data.truncated ? "TRUNCATED — capped read" : "all live assets"} tone={data.truncated ? "amber" : undefined} />
        <Tile label="Brands" value={data.brands ?? 0} />
        <Tile label="Clear enough to confirm" value={confirmable} tone="green" sub="≥3 assets, clear lead" />
        <Tile label="Need a human call" value={needsCall} tone={needsCall ? "amber" : "green"} sub="thin or split history" />
      </div>

      {data.assets_without_brand > 0 && (
        <Card style={{ borderColor: "#d97706", marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: "var(--t2)" }}>
            <b style={{ color: "var(--t1)" }}>{data.assets_without_brand} assets carry no brand.</b>{" "}
            They were bulk-imported with no account link, so their crew history can't be attributed to a client.
            They're grouped under “(no brand)” below and can't seed a per-brand roster.
          </div>
        </Card>
      )}

      {(data.routed_crafts_never_staffed || []).length > 0 && (
        <Card style={{ borderColor: "var(--r)", marginBottom: 16 }}>
          <div style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: "var(--r)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 }}>
            Routed to, but never staffed
          </div>
          <div style={{ fontSize: 13, color: "var(--t2)" }}>
            {data.routed_crafts_never_staffed.join(" · ")}
            <div style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)", marginTop: 6 }}>
              The table sends work to these crafts but no asset has ever had one assigned. They must be filled by hand or that stage will never resolve.
            </div>
          </div>
        </Card>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: "1 1 220px", maxWidth: 320 }}>
          <Input placeholder="Filter brands…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button onClick={() => setOnlyGaps((v) => !v)}
          style={{ fontFamily: mono, fontSize: 11, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase",
            padding: "8px 13px", borderRadius: "var(--radius-sm)", cursor: "pointer",
            background: onlyGaps ? "var(--r)" : "transparent", color: onlyGaps ? "#fff" : "var(--t2)",
            border: onlyGaps ? "none" : "1px solid var(--bdr)" }}>
          Only brands with gaps
        </button>
      </div>

      {brands.length === 0 && <Empty>No brands match.</Empty>}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {brands.map((b) => (
          <Card key={b.brand} style={{ padding: 0 }}>
            <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--bdr)", display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "var(--t1)" }}>{b.brand}</span>
              <span style={{ fontFamily: mono, fontSize: 10, color: "var(--t3)", letterSpacing: ".06em", textTransform: "uppercase" }}>
                {(b.crafts || []).length} crafts seen
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                <thead>
                  <tr>
                    <th style={th}>Craft</th>
                    <th style={th}>Suggested</th>
                    <th style={th}>Evidence</th>
                    <th style={th}>Other names seen</th>
                  </tr>
                </thead>
                <tbody>
                  {(b.crafts || []).map((c) => {
                    const s = c.suggested;
                    const others = (c.candidates || []).filter((x) => !s || x.user_id !== s.user_id);
                    return (
                      <tr key={c.craft}>
                        <td style={{ ...td, fontFamily: mono, fontSize: 12, color: "var(--t1)", whiteSpace: "nowrap" }}>{c.label || c.craft}</td>
                        <td style={td}>
                          {s ? (
                            <>
                              <div style={{ color: "var(--t1)", fontWeight: 650 }}>{s.name}</div>
                              <div style={{ fontFamily: mono, fontSize: 10, color: "var(--t3)" }}>
                                {s.designation || s.role || "—"}
                              </div>
                            </>
                          ) : (
                            <Pill tone="silent">{BLOCK_REASON[c.suggested_blocked] || "unclear"}</Pill>
                          )}
                        </td>
                        <td style={{ ...td, fontFamily: mono, fontSize: 11 }}>
                          {s ? `${s.assets} of ${c.assets} assets · ${s.share_pct}%` : `${c.assets} assets total`}
                        </td>
                        <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>
                          {others.length
                            ? others.map((o) => `${o.name} (${o.assets})`).join(", ")
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

// ── Unowned work ────────────────────────────────────────────────────────────
function Unowned({ data, onOpen }) {
  const [onlyFully, setOnlyFully] = useState(false);
  if (!data) return <Empty>Checking every live asset for an owner…</Empty>;

  const rows = (data.assets || []).filter((r) => (onlyFully ? r.fully_unowned : true));

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 16 }}>
        <Tile label="Assets with no owner" value={data.count ?? 0} tone={data.count ? "amber" : "green"}
          sub="on their current stage" />
        <Tile label="Nobody at all" value={data.fully_unowned ?? 0} tone={data.fully_unowned ? "red" : "green"}
          sub="not even an escalation target" />
      </div>

      {(data.count ?? 0) === 0 ? (
        <Card><div style={{ fontFamily: mono, fontSize: 12, color: "#0ca30c" }}>
          Every live asset has someone in the craft that owns its current stage.
        </div></Card>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <button onClick={() => setOnlyFully((v) => !v)}
              style={{ fontFamily: mono, fontSize: 11, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase",
                padding: "8px 13px", borderRadius: "var(--radius-sm)", cursor: "pointer",
                background: onlyFully ? "var(--r)" : "transparent", color: onlyFully ? "#fff" : "var(--t2)",
                border: onlyFully ? "none" : "1px solid var(--bdr)" }}>
              Only the completely unowned
            </button>
          </div>
          <Card style={{ padding: 0, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 660 }}>
              <thead>
                <tr>
                  <th style={th}>Asset</th>
                  <th style={th}>Brand</th>
                  <th style={th}>Stage</th>
                  <th style={th}>Missing</th>
                  <th style={th}>Falls back to</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.asset_id} onClick={() => onOpen(r.asset_id)}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--s2)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <td style={{ ...td, color: "var(--t1)", fontWeight: 600 }}>{r.title || r.asset_id}</td>
                    <td style={{ ...td, fontFamily: mono, fontSize: 11 }}>{r.brand}</td>
                    <td style={{ ...td, fontFamily: mono, fontSize: 11, whiteSpace: "nowrap" }}>{r.stage}</td>
                    <td style={td}><Pill tone={r.fully_unowned ? "bad" : "silent"}>{r.missing_label || r.missing_craft}</Pill></td>
                    <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>
                      {r.fully_unowned
                        ? <span style={{ color: "var(--r)" }}>nobody</span>
                        : `${r.falls_back_to_craft}${r.fallback_person ? ` · ${r.fallback_person.name}` : ""}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </>
  );
}

// ── Preview drawer content ──────────────────────────────────────────────────
function PreviewPanel({ preview, loading }) {
  if (loading) return <Empty>Resolving…</Empty>;
  if (!preview) return null;
  return (
    <Card style={{ marginTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "var(--t1)" }}>{preview.title || preview.asset_id}</span>
        <span style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>{preview.stage}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t3)", marginBottom: 3 }}>Owning craft</div>
          <div style={{ fontSize: 14, fontWeight: 650, color: "var(--t1)" }}>{preview.owning_label || "—"}</div>
        </div>
        <div>
          <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t3)", marginBottom: 3 }}>Would assign to</div>
          <div style={{ fontSize: 14, fontWeight: 650, color: preview.unresolved ? "var(--r)" : "var(--t1)" }}>
            {preview.would_assign_to ? preview.would_assign_to.name : "nobody — unresolved"}
          </div>
          {preview.resolved_via && <div style={{ fontFamily: mono, fontSize: 10, color: "var(--t3)" }}>via {preview.resolved_via}</div>}
        </div>
        <div>
          <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t3)", marginBottom: 3 }}>Clock</div>
          <div style={{ fontSize: 14, fontWeight: 650, color: "var(--t1)" }}>{preview.sla_days ? `${preview.sla_days} days` : "—"}</div>
        </div>
      </div>
      <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t3)", marginBottom: 6 }}>Resolution chain</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {(preview.resolution_chain || []).map((c, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontFamily: mono, fontSize: 11,
            padding: "6px 9px", borderRadius: 4, background: c.uid ? "rgba(16,163,12,.08)" : "var(--s2)" }}>
            <span style={{ color: "var(--t2)" }}>{c.tier}</span>
            <span style={{ color: c.uid ? "#0ca30c" : "var(--t4)" }}>{c.person ? c.person.name : (c.uid ? c.uid : "empty")}</span>
          </div>
        ))}
      </div>
      {preview.resolved_uid && !preview.resolved_uid_known && (
        <div style={{ marginTop: 10, fontFamily: mono, fontSize: 11, color: "var(--r)" }}>
          The slot holds “{preview.resolved_uid}”, which matches no user — a departed account, or an email written where an id belongs.
        </div>
      )}
    </Card>
  );
}

export default function RoutingDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("ownership");
  const [table, setTable] = useState(null);
  const [roster, setRoster] = useState(null);
  const [rosterErr, setRosterErr] = useState("");
  const [unowned, setUnowned] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [assetId, setAssetId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const t = await api.get("/routing/table");
        if (!dead) setTable(t.data);
      } catch (e) {
        if (!dead) setLoadErr(e?.response?.data?.detail || "Routing module isn't responding — it may not be deployed yet.");
      }
      try {
        const u = await api.get("/routing/unresolved");
        if (!dead) setUnowned(u.data);
      } catch { /* surfaced by loadErr */ }
      try {
        const r = await api.get("/routing/roster-evidence");
        if (!dead) setRoster(r.data);
      } catch (e) {
        if (!dead) setRosterErr(e?.response?.status === 403
          ? "Roster evidence is admin-only."
          : (e?.response?.data?.detail || "Couldn't read roster evidence."));
      }
    })();
    return () => { dead = true; };
  }, []);

  const runPreview = async (id) => {
    const target = (id || assetId || "").trim();
    if (!target) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await api.get(`/routing/preview/${encodeURIComponent(target)}`);
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
      setLoadErr(e?.response?.data?.detail || "No asset with that id.");
    } finally {
      setPreviewing(false);
    }
  };

  const openFromList = (id) => { setTab("preview"); setAssetId(id); runPreview(id); };

  const tabs = useMemo(() => ([
    { k: "ownership", label: "Ownership map" },
    { k: "roster", label: "Roster evidence" },
    { k: "unowned", label: `Unowned work${unowned?.count ? ` (${unowned.count})` : ""}` },
    { k: "preview", label: "Preview an asset" },
  ]), [unowned]);

  return (
    <div style={{ padding: "24px 22px 80px", maxWidth: 1180, margin: "0 auto" }}>
      <PageTitle
        title="Routing"
        sub="Who owns the work at every stage — and who has actually been doing it. Read-only: nothing is assigned automatically yet."
        right={<Btn kind="ghost" onClick={() => navigate("/production")}>Production</Btn>}
      />

      {loadErr && (
        <Card style={{ borderColor: "var(--r)", marginBottom: 16 }}>
          <div style={{ color: "var(--r)", fontFamily: mono, fontSize: 12 }}>{loadErr}</div>
        </Card>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ fontFamily: mono, fontSize: 11, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase",
              padding: "8px 14px", borderRadius: "var(--radius-sm)", cursor: "pointer",
              background: tab === t.k ? "var(--r)" : "transparent", color: tab === t.k ? "#fff" : "var(--t2)",
              border: tab === t.k ? "none" : "1px solid var(--bdr)" }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "ownership" && <Ownership table={table} />}
      {tab === "roster" && <Roster data={roster} err={rosterErr} />}
      {tab === "unowned" && <Unowned data={unowned} onOpen={openFromList} />}
      {tab === "preview" && (
        <>
          <Card>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ flex: "1 1 260px" }}>
                <Input placeholder="Asset id, e.g. asset_9f2c1b0e4a77"
                  value={assetId}
                  onChange={(e) => setAssetId(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runPreview()} />
              </div>
              <Btn onClick={() => runPreview()} disabled={!assetId.trim()}>Resolve</Btn>
            </div>
            <div style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)", marginTop: 8 }}>
              Shows what the engine would do for that asset's current stage. Writes nothing.
              Rows in “Unowned work” open here directly.
            </div>
          </Card>
          <PreviewPanel preview={preview} loading={previewing} />
        </>
      )}
    </div>
  );
}
